import { getOverrideNoteTemplate } from 'config/Constants';
import { format } from 'date-fns-tz';
import { EnhancedEditJobsRequestSchema, Job, Note, Room } from 'Interfaces/JobsInterface';
import { UserInfoInterface } from 'Interfaces/User/userInterfaces';
import { debounce } from 'lodash';
import { computed, makeAutoObservable, runInAction, toJS } from 'mobx';
import { toast } from 'react-toastify';
import { AddJobFormData } from 'screens/JobPaste/components/AddJobModal/useAddJobModal';
import { SysproJob } from 'screens/JobPaste/types';
import { JobPasteServices } from 'services/jobPasteServices';
import { yjsCollaborationService } from 'services/yjsCollaborationService';
import { ShiftTimingStore } from 'stores/ReferenceStore/ShiftTimingsStore';
import { handleApiError } from 'utils/handleApiError';
import {
  displayISODateWithoutTimezoneConversion,
  formatDateToMMDDYYYY,
  formatJobStartTime
} from 'utils/jobStartTimeUtilities';
import { fetchJobsServerResponseToUIObject } from './utils';

export interface OverrideItem {
  jobId: string;
  action: string;
  valueBefore: string | number;
  valueNow: string | number;
  reason: string;
}

/**
 * JobPasteStore with search functionality
 */
export class JobPasteStore {
  jobs: Room[] | null = null;
  _cloned_jobs_for_edits: Room[] | null = null;
  isJobsLoading: boolean = false;
  activeIndexList: number[] = [];
  isEditMode: boolean = false;
  currentRoomInEditMode: string | null = null;
  shift: string | null = null;
  date: Date = new Date();
  searchQuery: string = '';
  reasonCaptureForOverrides: OverrideItem[] | null = null;
  saveSingleJobLoading: boolean = false;
  jobEditSaving: boolean = false;
  savingAllRooms: boolean = false;
  // Track Redis check loading state per room
  private redisCheckLoadingByRoom: Map<string, boolean> = new Map(); // roomId -> loading state
  debouncedFetchJobs: () => void;
  debouncedUpdateSharedFilter: (shift: string, date: Date) => void;
  environment = import.meta.env.VITE_ENVIRONMENT;

  // Syspro Jobs
  sysproJobs: SysproJob[] = [];
  isSysproJobsLoading: boolean = false;
  selectedSysproJobs: SysproJob[] = [];

  // Active Users tracking
  activeUsersByRoom: {
    [roomId: string]: {
      userCount: number;
      users: {
        [userId: string]: {
          username: string;
          joined_at: string;
        };
      };
    };
  } = {};

  // Field change tracking for dirty fields with timestamps
  private fieldChangeTracker: Map<string, Map<string, Map<string, number>>> = new Map(); // roomId -> jobId -> field -> timestamp

  constructor() {
    makeAutoObservable(this, {
      filteredJobs: computed,
      isPastView: computed,
      debouncedFetchJobs: false, // Exclude from being observable
      debouncedUpdateSharedFilter: false // Exclude from being observable
    });

    this.debouncedFetchJobs = debounce(this.fetchAllJobs, 500);
    this.debouncedUpdateSharedFilter = debounce(this.updateSharedFilter, 1000);
  }

  // Polling related methods
  setJobsLoading = (loading: boolean) => {
    this.isJobsLoading = loading;
  };

  /**
   * Track a field change with timestamp for a specific job
   * @param jobId - The ID of the job being edited
   * @param roomId - The ID of the room containing the job
   * @param field - The name of the field that was changed
   */
  trackFieldChange = (jobId: string, roomId: string, field: string) => {
    const timestamp = Date.now();

    // Initialize the nested maps if they don't exist
    if (!this.fieldChangeTracker.has(roomId)) {
      this.fieldChangeTracker.set(roomId, new Map());
    }

    const roomTracker = this.fieldChangeTracker.get(roomId);
    if (!roomTracker) {
      return;
    }

    if (!roomTracker.has(jobId)) {
      roomTracker.set(jobId, new Map());
    }

    const jobTracker = roomTracker.get(jobId);

    if (!jobTracker) {
      return;
    }

    jobTracker.set(field, timestamp);
  };

  /**
   * Track multiple field changes for bulk operations (like job reordering)
   * @param roomId - The ID of the room containing the jobs
   * @param fieldChanges - Array of field changes to track
   */
  trackBulkFieldChanges = (
    roomId: string,
    fieldChanges: Array<{ jobId: string; field: string }>
  ) => {
    const timestamp = Date.now(); // Use same timestamp for all changes in bulk operation

    // Initialize the nested maps if they don't exist
    if (!this.fieldChangeTracker.has(roomId)) {
      this.fieldChangeTracker.set(roomId, new Map());
    }

    const roomTracker = this.fieldChangeTracker.get(roomId);
    if (!roomTracker) {
      return;
    }

    // Track each field change
    fieldChanges.forEach(({ jobId, field }) => {
      if (!roomTracker.has(jobId)) {
        roomTracker.set(jobId, new Map());
      }

      const jobTracker = roomTracker.get(jobId);
      if (!jobTracker) {
        return;
      }

      jobTracker.set(field, timestamp);
    });
  };

  /**
   * Get tracked field changes for a specific job
   * @param jobId - The ID of the job
   * @param roomId - The ID of the room containing the job
   * @returns Array of field changes with timestamps
   */
  getTrackedFieldChanges = (
    jobId: string,
    roomId: string
  ): Array<{ field: string; timestamp: number }> => {
    const roomTracker = this.fieldChangeTracker.get(roomId);
    if (!roomTracker) {
      return [];
    }

    const jobTracker = roomTracker.get(jobId);
    if (!jobTracker) {
      return [];
    }

    return Array.from(jobTracker.entries()).map(([field, timestamp]) => ({
      field,
      timestamp
    }));
  };

  /**
   * Clear tracked field changes for a specific room (called when exiting edit mode)
   * @param roomId - The ID of the room
   */
  clearTrackedFieldChanges = (roomId: string) => {
    this.fieldChangeTracker.delete(roomId);
  };

  fetchJobsForPolling = async () => {
    try {
      const { rooms } = await JobPasteServices.fetch_jobs({
        date: format(new Date(this.date), 'yyyy-MM-dd'),
        shift: this.shift as string
      });

      const roomsWithColors = fetchJobsServerResponseToUIObject(rooms);
      return { rooms: roomsWithColors };
    } catch (error) {
      handleApiError(error, 'Error occurred while fetching jobs for polling');
    }
  };

  updateJobsNormal = (newRooms: Room[]) => {
    // Case 1: Normal mode - replace all jobs
    runInAction(() => {
      // Sort the rooms by ID to maintain consistent ordering
      const sortedRooms = [...newRooms].sort((a, b) => {
        // Convert room IDs to numbers for proper numeric sorting
        const roomIdA = parseInt(a.id, 10);
        const roomIdB = parseInt(b.id, 10);
        return roomIdA - roomIdB;
      });

      this.activeIndexList = sortedRooms
        .map((room, index) => (room.isExpanded ? index : -1))
        .filter(index => index !== -1);
      this.jobs = sortedRooms;
    });
  };

  updateJobsOptimized = (newRooms: Room[]) => {
    // Case 2: Edit mode - compare and append new jobs
    if (!this.jobs) {
      // If no existing data, just replace
      this.updateJobsNormal(newRooms);
      return;
    }

    runInAction(() => {
      // Create a map of existing job IDs for quick lookup in both current and cloned data
      const existingJobIds = new Map<string, boolean>();
      const clonedJobIds = new Map<string, boolean>();

      // Collect existing job IDs from current jobs
      this.jobs?.forEach(room => {
        room.jobs.forEach(job => {
          existingJobIds.set(job.jobId, true);
        });
      });

      // Collect existing job IDs from cloned jobs (if they exist)
      if (this._cloned_jobs_for_edits) {
        this._cloned_jobs_for_edits.forEach(room => {
          room.jobs.forEach(job => {
            clonedJobIds.set(job.jobId, true);
          });
        });
      }

      // Process new rooms and append only new jobs
      newRooms.forEach(newRoom => {
        const existingRoom = this.jobs?.find(room => room.id === newRoom.id);
        const clonedRoom = this._cloned_jobs_for_edits?.find(room => room.id === newRoom.id);

        if (existingRoom) {
          // Find new jobs that don't exist in current data
          const newJobs = newRoom.jobs.filter(job => !existingJobIds.has(job.jobId));

          if (newJobs.length > 0) {
            // Append new jobs to the end of the existing room in current jobs
            existingRoom.jobs.push(...newJobs);

            // Also append to cloned jobs if they exist
            if (clonedRoom) {
              clonedRoom.jobs.push(...newJobs);
            } else if (this._cloned_jobs_for_edits) {
              // If cloned room doesn't exist, create it with the new jobs
              this._cloned_jobs_for_edits.push({
                ...newRoom,
                jobs: newJobs
              });
            }
          }
        } else {
          // If room doesn't exist, add it to both current and cloned data
          if (this.jobs) {
            this.jobs.push(newRoom);
          }
          if (this._cloned_jobs_for_edits) {
            this._cloned_jobs_for_edits.push(newRoom);
          }
        }
      });

      // Force MobX to recognize the change by creating a new reference
      if (this._cloned_jobs_for_edits) {
        this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
      }
    });
  };

  // Methods related to Syspro Jobs
  fetchSysproJobs = async () => {
    this.isSysproJobsLoading = true;
    try {
      const response = await JobPasteServices.fetchSysproJobs();
      this.sysproJobs = response.jobs.map(job => ({
        ...job,
        confirmed_date: job.confirmed_date
          ? (() => {
              const d = new Date(job.confirmed_date);
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${mm}-${dd}-${yyyy}`;
            })()
          : ''
      }));
    } catch (error) {
      handleApiError(error, 'Error occurred while fetching Syspro jobs');
    } finally {
      this.isSysproJobsLoading = false;
    }
  };

  toggleSysproJobSelection = (jobId: string) => {
    const jobIndex = this.selectedSysproJobs.findIndex(job => job.job_id === jobId);

    if (jobIndex === -1) {
      // Job not selected yet, add it
      const job = this.sysproJobs.find(job => job.job_id === jobId);
      if (job) {
        this.selectedSysproJobs.push(job);
      }
    } else {
      // Job already selected, remove it
      this.selectedSysproJobs.splice(jobIndex, 1);
    }
  };

  selectAllSysproJobs = () => {
    if (this.selectedSysproJobs.length === this.sysproJobs.length) {
      // If all jobs are already selected, deselect all
      this.selectedSysproJobs = [];
    } else {
      // Otherwise select all jobs
      this.selectedSysproJobs = [...this.sysproJobs];
    }
  };

  clearSelectedSysproJobs = () => {
    this.selectedSysproJobs = [];
  };

  get isPastView(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison

    const selectedDate = new Date(this.date);
    selectedDate.setHours(0, 0, 0, 0); // Set to start of day for comparison

    return selectedDate < today;
  }

  // Computed property to get jobs for a specific room
  getJobsForRoom = (roomId: string) => {
    // If there's a search query, use filtered jobs
    if (this.searchQuery.trim()) {
      const filteredRoom = this.filteredJobs?.find(room => room.id === roomId);
      return filteredRoom ? filteredRoom.jobs : [];
    }

    // Otherwise use the normal logic
    if (this.isEditMode && this._cloned_jobs_for_edits) {
      const clonedRoom = this._cloned_jobs_for_edits.find(room => room.id === roomId);
      return clonedRoom ? clonedRoom.jobs : [];
    }

    const room = this.jobs?.find(room => room.id === roomId);
    return room ? room.jobs : [];
  };

  // Computed property to get total number of jobs for debugging
  get totalJobsCount(): number {
    if (this.isEditMode && this._cloned_jobs_for_edits) {
      return this._cloned_jobs_for_edits.reduce((total, room) => total + room.jobs.length, 0);
    }
    return this.jobs ? this.jobs.reduce((total, room) => total + room.jobs.length, 0) : 0;
  }

  get filteredJobs(): Room[] | null {
    // When in edit mode, use cloned jobs for filtering
    const jobsToFilter =
      this.isEditMode && this._cloned_jobs_for_edits ? this._cloned_jobs_for_edits : this.jobs;

    if (!jobsToFilter || !this.searchQuery.trim()) {
      return jobsToFilter;
    }

    // Create deep clone of the job data to avoid modifying original
    const filteredRooms = toJS(jobsToFilter).map(room => {
      // Filter jobs within each room based on the search query
      const filteredJobs = room.jobs.filter(job => this.jobMatchesSearch(job));

      // Return room with filtered jobs
      return {
        ...room,
        jobs: filteredJobs
      };
    });

    // Only include rooms that have at least one job matching the search
    return filteredRooms.filter(room => room.jobs.length > 0);
  }

  // Add these methods to the JobPasteStore class

  /**
   * Adds a new note to a job in the current jobs list
   * @param jobId The ID of the job to add a note to
   * @param note The note object to add
   */
  addNoteToJob = (jobId: string, note: Note) => {
    runInAction(() => {
      if (!this.jobs) {
        return;
      }

      // Find the job in main jobs and add the note
      let foundInMainJobs = false;
      for (const room of this.jobs) {
        const job = room.jobs.find(job => job.jobId === jobId); // Changed from job.id to job.jobId
        if (job) {
          // Add the note to the beginning of the notes array
          job.notes.unshift(note);
          foundInMainJobs = true;
          break;
        }
      }

      // Also update cloned jobs if in edit mode
      if (this._cloned_jobs_for_edits) {
        let foundInClonedJobs = false;
        for (const room of this._cloned_jobs_for_edits) {
          const job = room.jobs.find(job => job.jobId === jobId); // Changed from job.id to job.jobId
          if (job) {
            // Add the note to the beginning of the notes array
            job.notes.unshift(note);
            foundInClonedJobs = true;
            break;
          }
        }
      }

      // Force MobX reactivity by creating new array references
      if (this.jobs) {
        this.jobs = [...this.jobs];
      }
      if (this._cloned_jobs_for_edits) {
        this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
      }
    });
  };

  /**
   * Updates an existing note in a job
   * @param jobId The ID of the job containing the note
   * @param noteId The ID of the note to update
   * @param updatedNote The updated note object
   */
  updateNoteInJob = (jobId: string, noteId: number, updatedNote: Note) => {
    runInAction(() => {
      if (!this.jobs) {
        return;
      }

      // Find the job in main jobs and update the note
      for (const room of this.jobs) {
        const job = room.jobs.find(job => job.jobId === jobId); // Changed from job.id to job.jobId
        if (job) {
          const noteIndex = job.notes.findIndex(note => note.id === noteId);
          if (noteIndex !== -1) {
            // Update the note
            job.notes[noteIndex] = {
              ...job.notes[noteIndex],
              ...updatedNote,
              isEdited: true
            };
            break;
          }
        }
      }

      // Also update cloned jobs if in edit mode
      if (this._cloned_jobs_for_edits) {
        for (const room of this._cloned_jobs_for_edits) {
          const job = room.jobs.find(job => job.jobId === jobId); // Changed from job.id to job.jobId
          if (job) {
            const noteIndex = job.notes.findIndex(note => note.id === noteId);
            if (noteIndex !== -1) {
              // Update the note
              job.notes[noteIndex] = {
                ...job.notes[noteIndex],
                ...updatedNote,
                isEdited: true
              };
              break;
            }
          }
        }
      }

      // Force MobX reactivity by creating new array references
      if (this.jobs) {
        this.jobs = [...this.jobs];
      }
      if (this._cloned_jobs_for_edits) {
        this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
      }
    });
  };

  // Check if a job matches the search query across multiple fields
  jobMatchesSearch = (job: Job): boolean => {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      return true;
    } // Empty query matches all jobs

    // Search across multiple job fields
    return (
      (job.jobId && String(job.jobId).toLowerCase().includes(query)) ||
      (job.ndc && String(job.ndc).toLowerCase().includes(query)) ||
      (job.description && String(job.description).toLowerCase().includes(query)) ||
      (job.status && String(job.status).toLowerCase().includes(query)) ||
      (job.type && String(job.type).toLowerCase().includes(query)) ||
      (job.room && String(job.room).toLowerCase().includes(query)) ||
      (job.priority && String(job.priority).toLowerCase().includes(query))
    );
  };

  // Set search query
  setSearchQuery = (query: string) => {
    this.searchQuery = query;

    // Automatically expand accordions with matching results
    if (this.jobs && query.trim()) {
      this.expandRoomsWithMatches();
    } else if (!query.trim()) {
      // Reset to default accordion state when search is cleared
      this.resetAccordionState();
    }
  };

  // Expand rooms that have jobs matching the search query
  expandRoomsWithMatches = () => {
    if (!this.filteredJobs) {
      return;
    }

    this.activeIndexList = this.filteredJobs.map((_, index) => index).filter(index => index !== -1);
  };

  // Reset accordion state to what it was before searching
  resetAccordionState = () => {
    if (!this.jobs) {
      return;
    }

    this.activeIndexList = this.jobs
      .map((room, index) => (room.isExpanded ? index : -1))
      .filter(index => index !== -1);
  };

  fetchAllJobs = async () => {
    this.isJobsLoading = true;

    try {
      const { rooms } = await JobPasteServices.fetch_jobs({
        date: format(new Date(this.date), 'yyyy-MM-dd'),
        shift: this.shift as string
      });

      const roomsWithColors = fetchJobsServerResponseToUIObject(rooms);

      // Sort the rooms by ID to maintain consistent ordering
      const sortedRoomsWithColors = [...roomsWithColors].sort((a, b) => {
        // Convert room IDs to numbers for proper numeric sorting
        const roomIdA = parseInt(a.id, 10);
        const roomIdB = parseInt(b.id, 10);
        return roomIdA - roomIdB;
      });

      this.activeIndexList = sortedRoomsWithColors
        .map((room, index) => (room.isExpanded ? index : -1))
        .filter(index => index !== -1);
      this.jobs = sortedRoomsWithColors;
    } catch (error) {
      handleApiError(error, 'Some error occurred while fetching the job information');
    } finally {
      this.isJobsLoading = false;
    }
  };

  fetchSharedFilter = async () => {
    try {
      const response = await JobPasteServices.getSharedFilter();
      return response.data;
    } catch (error) {
      handleApiError(error, 'Error occurred while fetching shared filter');
      return null;
    }
  };

  initializeWithSharedFilter = async (shiftTimingStore: ShiftTimingStore) => {
    // Fetch shared filter first
    const sharedFilterData = await this.fetchSharedFilter();

    if (sharedFilterData && sharedFilterData.shift && sharedFilterData.filter_date) {
      // Use shared filter values if they exist
      // this.setShift(sharedFilterData.shift);
      const [year, month, day] = sharedFilterData.filter_date.split('-').map(Number);
      this.date = new Date(year, month - 1, day);
      this.shift = sharedFilterData.shift;
    } else {
      // Use current date and first shift as fallback
      const currentDate = new Date();
      this.date = currentDate;

      // Wait for shift timings to load, then set the first shift
      if (!shiftTimingStore.shiftTimings.length) {
        await shiftTimingStore.fetchShiftTimings(1, 100);
      }
      if (shiftTimingStore.shiftTimings.length > 0) {
        this.shift = shiftTimingStore.shiftTimings[0].shift_name;
      }
    }

    this.fetchAllJobs();
  };

  saveSingleJob = async (jobDataToSave: AddJobFormData, isReopen: boolean = false) => {
    if (!jobDataToSave) {
      toast.error('No job data provided');
      return;
    }

    // Format job_start_time if it's not null
    const formattedJobData = {
      ...jobDataToSave,
      job_start_time: jobDataToSave.job_start_time
        ? formatJobStartTime(new Date(jobDataToSave.job_start_time))
        : null
    };

    this.saveSingleJobLoading = true;

    try {
      const response = await JobPasteServices.saveJob(formattedJobData as any);

      // Show appropriate success toast based on operation type
      if (isReopen) {
        toast.success('Job reopened successfully');
      } else {
        toast.success('Job added successfully');
      }

      // Backend handles WebSocket broadcasting automatically
      // No need to send WebSocket message from frontend
      // WebSocket will automatically update the jobs list
      return response;
    } catch (error: any) {
      handleApiError(error, 'Error occured while saving job');
    } finally {
      this.saveSingleJobLoading = false;
    }
  };

  enableEditMode = async (roomId: string) => {
    try {
      // Validate roomId
      if (!roomId || !roomId.trim()) {
        throw new Error('Invalid room ID');
      }

      // Set loading state for this specific room
      runInAction(() => {
        this.redisCheckLoadingByRoom.set(roomId, true);
      });

      this._cloned_jobs_for_edits = toJS(this.jobs);

      // Check if there's updated data in Redis
      const redisResponse = await JobPasteServices.checkRedisDataForRoom(roomId);
      if (
        redisResponse.check &&
        redisResponse.updated_data &&
        Array.isArray(redisResponse.updated_data.data.jobs)
      ) {
        // Update the jobs array with the Redis data
        const updatedJobs = toJS(this._cloned_jobs_for_edits);
        const roomIndex = updatedJobs?.findIndex(room => room.id === roomId);

        if (roomIndex !== undefined && roomIndex !== -1 && updatedJobs) {
          // Use updateRoomWithCRDTData to properly handle Redis data conversion and state updates
          const crdtData: { jobs: Record<string, any> } = { jobs: {} };

          // Check if we have Redis data to convert
          if (redisResponse.updated_data?.data?.jobs?.length > 0) {
            // Convert the Redis jobs array to CRDT format for the existing method
            // Only update specific fields: priority, status, room, bpmOverride, cleanOverride, numberOfBottlesInAtShiftStart, jobStartTime, notes
            redisResponse.updated_data.data.jobs.forEach(job => {
              if (job && job.jobId) {
                crdtData.jobs[job.jobId] = {
                  priority: { value: job.priority },
                  status: { value: job.status },
                  room: { value: job.room },
                  bpmOverride: { value: job.bpmOverride },
                  cleanOverride: { value: job.cleanOverride },
                  numberOfBottlesInAtShiftStart: { value: job.numberOfBottlesInAtShiftStart },
                  jobStartTime: { value: job.jobStartTime },
                  notes: { value: job.notes || [] }
                };
              }
            });

            // Use the existing method to update the room data
            this.updateRoomWithCRDTData(roomId, crdtData);
          }
        }
      }
    } catch (error) {
      // Fallback to normal edit mode if Redis check fails
      runInAction(() => {
        this.clearTrackedFieldChanges(roomId);
      });

      // Show error notification with more specific error message
      let errorMessage = 'Failed to check for updated data. Using current data for editing.';
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Using current data for editing.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Using current data for editing.';
        } else if (error.message.includes('Cannot read properties')) {
          errorMessage = 'Data format error. Using current data for editing.';
        }
      }
      toast.error(errorMessage);
    } finally {
      // Reset loading state for this specific room
      runInAction(() => {
        this.redisCheckLoadingByRoom.delete(roomId);
        this.isEditMode = true;
        this.currentRoomInEditMode = roomId;
      });
    }
  };

  disableEditMode = () => {
    // Generate revert payload before resetting edit mode
    if (this.currentRoomInEditMode && this._cloned_jobs_for_edits && this.jobs) {
      const revertPayload = this.generateRevertPayload(this.currentRoomInEditMode);
      if (revertPayload) {
        // console.log('🔄 Revert Payload:', revertPayload);
        // Send revert notification to other users via WebSocket
        this.sendRevertNotification(revertPayload);
      }
    }

    this.isEditMode = false;
    // Clear tracked field changes for the room that was being edited before setting to null
    if (this.currentRoomInEditMode) {
      this.clearTrackedFieldChanges(this.currentRoomInEditMode);
      // Also clear any loading state for this room
      this.redisCheckLoadingByRoom.delete(this.currentRoomInEditMode);
    }
    this.currentRoomInEditMode = null;
    // When exiting edit mode, sync the cloned data back to main jobs
    if (this._cloned_jobs_for_edits) {
      this._cloned_jobs_for_edits = toJS(this.jobs);
    }
  };

  /**
   * Generate revert payload when user cancels editing
   * Compares original jobs with edited jobs and creates payload with reverted values
   */
  private generateRevertPayload = (roomId: string) => {
    if (!this._cloned_jobs_for_edits || !this.jobs) {
      return null;
    }

    // Find the original and edited room data
    const originalRoom = this.jobs.find(room => room.id === roomId);
    const editedRoom = this._cloned_jobs_for_edits.find(room => room.id === roomId);

    if (!originalRoom || !editedRoom) {
      return null;
    }

    // Get current user email for the revertedBy field
    const currentUserEmail = localStorage.getItem('user_name') || 'Unknown User';

    const reverts: Record<string, any> = {};

    // Iterate through each job in the edited room to find changes
    editedRoom.jobs.forEach(editedJob => {
      // Find the corresponding job in the original room
      const originalJob = originalRoom.jobs.find(job => job.jobId === editedJob.jobId);

      if (!originalJob) {
        return;
      }

      const jobReverts: Record<string, any> = {};

      // Check for changes in editable fields and collect original values
      const fieldsToCheck = [
        'bpmOverride',
        'cleanOverride',
        'serial',
        'priority',
        'status',
        'room',
        'numberOfBottlesInAtShiftStart',
        'jobStartTime',
        'notes'
      ];

      fieldsToCheck.forEach(field => {
        const originalValue = (originalJob as any)[field];
        const editedValue = (editedJob as any)[field];

        // Compare values, handling different data types
        let hasChanged = false;

        if (field === 'notes') {
          // For notes array, do a deep comparison
          hasChanged = JSON.stringify(originalValue) !== JSON.stringify(editedValue);
        } else if (field === 'jobStartTime') {
          // Handle date comparison
          const originalDate = originalValue ? new Date(originalValue).getTime() : null;
          const editedDate = editedValue ? new Date(editedValue).getTime() : null;
          hasChanged = originalDate !== editedDate;
        } else {
          // For other fields, direct comparison
          hasChanged = originalValue !== editedValue;
        }

        if (hasChanged) {
          jobReverts[field] = originalValue;
        }
      });

      // If there are any reverts for this job, add it to the payload
      if (Object.keys(jobReverts).length > 0) {
        reverts[originalJob.jobId] = jobReverts;
      }
    });

    // Only return payload if there are actual reverts
    if (Object.keys(reverts).length === 0) {
      return null;
    }

    return {
      room: originalRoom.name,
      revertedBy: currentUserEmail,
      reverts: reverts
    };
  };

  /**
   * Send revert notification to other users via WebSocket
   * Notifies other users that changes have been reverted with details of what was reverted
   */
  private sendRevertNotification = (revertPayload: any) => {
    try {
      if (yjsCollaborationService.isWebSocketConnected()) {
        yjsCollaborationService.sendChangesReverted(revertPayload.room, revertPayload.reverts);
      } else {
        console.warn(
          '⚠️ YJS WebSocket not connected, revert notification will not be sent to other users'
        );
      }
    } catch (error) {
      console.error('Failed to send revert notification:', error);
    }
  };

  setActiveIndex = (index: number[]) => {
    this.activeIndexList = index;
  };

  setShift = (shift: string | null) => {
    this.shift = shift;

    // Update shared filter when shift changes (only if both shift and date are available)
    // if (shift && this.date) {
    //   this.debouncedUpdateSharedFilter(shift, this.date);
    // }
  };

  setDate = (date: Date) => {
    this.date = date;

    // If changing to past view mode while in edit mode, disable edit mode
    // if (this.isPastView && this.isEditMode) {
    //   this.disableEditMode();
    // }

    // Fetch new data after a short delay
    // this.debouncedFetchJobs();

    // Update shared filter when date changes (only if both shift and date are available)
    // if (this.shift && date) {
    //   this.debouncedUpdateSharedFilter(this.shift, date);
    // }
  };

  onSaveOfEditedJobsInARoom = (roomId: string) => {
    if (!this._cloned_jobs_for_edits) {
      toast.error('Cannot save changes: Job data is missing');
      return;
    }

    // Find the original room data for comparison using stored original data
    const originalRoom = this.jobs?.find(room => room.id === roomId);
    const editedRoom = this._cloned_jobs_for_edits.find(room => room.id === roomId);

    if (!originalRoom || !editedRoom) {
      toast.error(`Room with ID ${roomId} not found`);
      return;
    }

    const overrideItems: OverrideItem[] = [];

    // Iterate through each job in the edited room
    editedRoom.jobs.forEach(editedJob => {
      // Find the corresponding job in the original room
      const originalJob = originalRoom.jobs.find(job => job.jobId === editedJob.jobId);

      if (!originalJob) {
        return;
      }

      // Check for room changes
      if (originalJob.room !== editedJob.room) {
        overrideItems.push({
          jobId: originalJob.jobId,
          action: 'room change',
          valueBefore: originalJob.room,
          valueNow: editedJob.room,
          reason: ''
        });
      }

      // Check for BPM override changes
      if (originalJob.bpmOverride !== editedJob.bpmOverride) {
        overrideItems.push({
          jobId: originalJob.jobId,
          action: 'bpm override',
          valueBefore: (originalJob.bpmOverride ?? originalJob.bpmProjected) || 0,
          valueNow: editedJob.bpmOverride || 0,
          reason: ''
        });
      }

      // Check for clean override changes
      if (originalJob.cleanOverride !== editedJob.cleanOverride) {
        overrideItems.push({
          jobId: originalJob.jobId,
          action: 'clean override',
          valueBefore: (originalJob.cleanOverride ?? originalJob.cleanProjected) || '',
          valueNow: editedJob.cleanOverride || '',
          reason: ''
        });
      }
    });

    // Set the override items
    this.reasonCaptureForOverrides = overrideItems;

    // If there are overrides, let the user know
    if (overrideItems.length > 0) {
      toast.info(`Found ${overrideItems.length} changes that require reasons`);
      return overrideItems.length;
    } else {
      // If no overrides are needed, proceed with save
      return undefined;
    }
  };

  addOverrideReasonToJobNotes = (overrideReasons: OverrideItem[], user: UserInfoInterface) => {
    const notes = overrideReasons.map(reason => ({
      job_id: reason.jobId,
      note: getOverrideNoteTemplate(reason, user)
    }));

    // Create a deep copy of this.jobs
    const updatedJobs = toJS(this._cloned_jobs_for_edits);

    if (updatedJobs) {
      let timestampOffset = 0; // To ensure unique timestamps
      notes.forEach(note => {
        updatedJobs.forEach((room, index) => {
          const jobIndex = room.jobs.findIndex(item => item.jobId === note.job_id);
          if (jobIndex !== -1) {
            // Generate a unique ID for the new note
            const maxNoteId = Math.max(...room.jobs[jobIndex].notes.map(n => n.id), 0);
            // Create timestamp with offset to ensure uniqueness
            const noteTimestamp = new Date(Date.now() + timestampOffset).toISOString();
            timestampOffset += 100; // Add 100ms for each subsequent note

            const newNote = {
              id: maxNoteId + 1,
              created_at: noteTimestamp,
              createdBy: user.userInfo.name,
              noteText: note.note,
              updated_at: noteTimestamp
            };

            // Add the note and sort by timestamp to maintain chronological order
            updatedJobs[index].jobs[jobIndex].notes.push(newNote);
            updatedJobs[index].jobs[jobIndex].notes.sort(
              (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
          }
        });
      });
    }

    // Update this.jobs with the modified copy
    this._cloned_jobs_for_edits = updatedJobs;
  };

  /**
   * Compares original and edited job data to identify changed fields
   * @param originalJob - The original job data
   * @param editedJob - The edited job data
   * @returns Array of field names that have changed
   */
  private getDirtyFields = (originalJob: Job, editedJob: Job): string[] => {
    const dirtyFields: string[] = [];

    // Define the fields to check for changes
    const fieldsToCheck = [
      'serial', // Added serial field for job reordering detection
      'room',
      'priority',
      'status',
      'ndc',
      'description',
      'qtyOfContainers',
      'qtyPerContainer',
      'type',
      'qtyUnitsToPackage',
      'confirmedDate',
      'bpmOverride',
      'cleanOverride',
      'numberOfBottlesInAtShiftStart',
      'jobStartTime'
    ];

    fieldsToCheck.forEach(field => {
      const originalValue = (originalJob as any)[field];
      const editedValue = (editedJob as any)[field];

      // Handle date comparisons
      if (field === 'confirmedDate' || field === 'jobStartTime') {
        const originalDate = originalValue ? new Date(originalValue).getTime() : null;
        const editedDate = editedValue ? new Date(editedValue).getTime() : null;
        if (originalDate !== editedDate) {
          dirtyFields.push(field);
        }
      } else if (originalValue !== editedValue) {
        // Handle null/undefined comparisons
        dirtyFields.push(field);
      }
    });

    return dirtyFields;
  };

  /**
   * Prepares job data from the edited room for saving to the backend
   * Includes enhanced metadata for change tracking and audit purposes
   * Only includes jobs that have been actually modified or reordered
   *
   * @param roomId - The ID of the room being edited
   * @returns An object with room information, jobs array, and change tracking metadata
   *
   * Enhanced Payload Structure:
   * - roomId: Room identifier
   * - roomName: Room display name
   * - date: Current date in ISO format
   * - shift: Current shift
   * - jobs: Array of modified job objects with dirty_fields included directly in each job
   * - timestamp: Epoch timestamp when save was initiated
   */
  prepareJobDataForSave = (roomId: string): EnhancedEditJobsRequestSchema | null => {
    if (!this._cloned_jobs_for_edits) {
      toast.error('No edited data to save');
      return null;
    }

    const editedRoom = this._cloned_jobs_for_edits.find(room => room.id === roomId);

    if (!editedRoom) {
      toast.error(`Room with ID ${roomId} not found in edited data`);
      return null;
    }

    // Find the original room data for comparison using stored original data
    const originalRoom = this.jobs?.find(room => room.id === roomId);
    if (!originalRoom) {
      toast.error('Original room data not found for comparison');
      return null;
    }

    // Process each job to include its dirty fields with timestamps
    // Only include jobs that have been actually modified
    const jobsWithDirtyFields = editedRoom.jobs
      .map(editedJob => {
        const originalJob = originalRoom.jobs.find(job => job.jobId === editedJob.jobId);

        if (!originalJob) {
          return null; // Skip jobs that don't have original data
        }

        const dirtyFields = this.getDirtyFields(originalJob, editedJob);

        // Get tracked field changes with timestamps
        const trackedChanges = this.getTrackedFieldChanges(editedJob.jobId, roomId);

        // Combine dirty fields with tracked changes, ensuring no duplicates
        const dirtyFieldsWithTimestamps: Array<{ field: string; timestamp: number }> = [];

        // Add tracked changes first (these have timestamps and should always be included)
        trackedChanges.forEach(change => {
          dirtyFieldsWithTimestamps.push(change);
        });

        // Add any remaining dirty fields that weren't tracked (fallback with current timestamp)
        dirtyFields.forEach(field => {
          if (!dirtyFieldsWithTimestamps.some(change => change.field === field)) {
            dirtyFieldsWithTimestamps.push({
              field,
              timestamp: Date.now()
            });
          }
        });

        // Only return jobs that have actual changes (dirty fields or tracked changes)
        if (dirtyFieldsWithTimestamps.length > 0) {
          return {
            ...editedJob,
            confirmedDate: editedJob.confirmedDate
              ? new Date(editedJob.confirmedDate).toISOString()
              : '',
            jobStartTime: editedJob.jobStartTime
              ? formatJobStartTime(new Date(editedJob.jobStartTime))
              : null,
            dirty_fields: dirtyFieldsWithTimestamps // Include dirty fields with timestamps directly in the job object
          };
        }

        // Return null for unchanged jobs (will be filtered out)
        return null;
      })
      .filter(job => job !== null) as any[]; // Filter out null values (unchanged jobs)

    // Structure the enhanced payload as needed for the backend
    const payload = {
      roomId: editedRoom.id,
      roomName: editedRoom.name,
      date: this.date.toISOString(),
      shift: this.shift || '', // Handle null case for shift
      jobs: jobsWithDirtyFields, // Only modified jobs with dirty fields included directly
      timestamp: Date.now() // Epoch timestamp when save was initiated
    };

    return payload;
  };

  /**
   * Updates the frontend state with CRDT merged data from Redis
   * Converts CRDT format back to frontend format and updates the store
   * @param roomId - The ID of the room to update
   * @param crdtData - The merged CRDT data from Redis
   */
  updateRoomWithCRDTData = (roomId: string, crdtData: { jobs: Record<string, any> }) => {
    try {
      // Find the room in the current jobs array
      const roomIndex = this._cloned_jobs_for_edits?.findIndex(room => room.id === roomId);
      if (roomIndex === undefined || roomIndex === -1) {
        return;
      }

      // Get the current jobs for this room and update only dirty fields
      const currentRoom = this._cloned_jobs_for_edits![roomIndex];
      const updatedJobs = [...currentRoom.jobs]; // Create a copy of current jobs

      // Update only the fields that have dirty_fields in the Redis response
      Object.entries(crdtData.jobs).forEach(([jobId, crdtJob]) => {
        // Find the existing job in the current jobs array by jobId
        const existingJobIndex = updatedJobs.findIndex(job => job.jobId === jobId);

        if (existingJobIndex !== -1) {
          const existingJob = updatedJobs[existingJobIndex];
          const updatedJob = { ...existingJob }; // Create a copy of existing job

          // Only update fields that have dirty_fields
          if (crdtJob.dirty_fields && Array.isArray(crdtJob.dirty_fields)) {
            crdtJob.dirty_fields.forEach((dirtyField: any) => {
              const fieldName = dirtyField.field;
              const fieldValue = crdtJob[fieldName]?.value;

              if (fieldValue !== undefined) {
                (updatedJob as any)[fieldName] = fieldValue;
              }
            });

            // Update the job in the array
            updatedJobs[existingJobIndex] = toJS(updatedJob);
            updatedJobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
          }
        }
      });

      // Update the room with the new jobs
      runInAction(() => {
        if (this._cloned_jobs_for_edits) {
          // Replace the entire _cloned_jobs_for_edits array to ensure MobX reactivity
          this._cloned_jobs_for_edits = this._cloned_jobs_for_edits.map((room, idx) =>
            idx === roomIndex ? { ...room, jobs: updatedJobs } : room
          );
          this.jobs = toJS(this._cloned_jobs_for_edits);
        }
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error(`❌ Error updating room ${roomId} with CRDT data:`, error);
      }
      toast.error(`Error updating room ${roomId} with CRDT data!`);
    }
  };

  /**
   * Saves the edited jobs for a room to the backend
   * Uses enhanced payload structure with change tracking metadata
   * @param roomId - The ID of the room being saved
   */
  saveEditedRoomJobs = async (roomId: string) => {
    try {
      this.jobEditSaving = true;
      const payload = this.prepareJobDataForSave(roomId);

      if (!payload) {
        return;
      }

      // Call the CRDT endpoint to process the edit
      const response = await JobPasteServices.saveEditedRoomJobs(payload);

      if (response && response.status === 'success') {
        // Show success message
        toast.success(`Jobs for ${payload.roomName} have been updated`);

        // Update the frontend state with the merged data from Redis
        if (response.merged_data && response.merged_data.jobs) {
          this.updateRoomWithCRDTData(roomId, response.merged_data);
        }

        if (response.task_warning) {
          toast.warning(`Background processing warning: ${response.task_warning}`);
        }

        // Clear tracked field changes for the room that was saved
        this.clearTrackedFieldChanges(roomId);

        // Exit edit mode
        this.disableEditMode();
      } else {
        // Handle error response
        const errorMessage = response?.error || response?.message || 'Unknown error occurred';
        toast.error(`Failed to save jobs: ${errorMessage}`);
      }
    } catch (error: any) {
      handleApiError(error, 'Error saving jobs');
    } finally {
      this.jobEditSaving = false;
    }
  };

  /**
   * Gets the notes for a specific job
   * @param jobId The ID of the job to get notes for
   * @returns The notes array for the job, or undefined if not found
   */
  getJobNotes = (jobId: string): Note[] | undefined => {
    // If we're in edit mode, check cloned jobs first
    if (this.isEditMode && this._cloned_jobs_for_edits) {
      for (const room of this._cloned_jobs_for_edits) {
        const job = room.jobs.find(job => job.jobId === jobId);
        if (job) {
          return job.notes;
        }
      }
    }

    // Otherwise, check the main jobs
    if (!this.jobs) {
      return undefined;
    }

    for (const room of this.jobs) {
      const job = room.jobs.find(job => job.jobId === jobId);
      if (job) {
        return job.notes;
      }
    }
    return undefined;
  };

  updateSharedFilter = async (shift: string, date: Date) => {
    try {
      const response = await JobPasteServices.updateSharedFilter({
        shift,
        filter_date: format(date, 'yyyy-MM-dd')
      });

      return response;
    } catch (error) {
      // Don't show error toast for this as it's a background operation
      if (this.environment === 'development') {
        console.error('Error updating shared filter:', error);
      }
      toast.error('Error updating shared filter!');
    }
  };

  /**
   * Saves all edited rooms with their jobs to the backend
   * This method handles saving all rooms at once and provides detailed error reporting
   */
  saveAllEditedRooms = async () => {
    if (!this._cloned_jobs_for_edits || this._cloned_jobs_for_edits.length === 0) {
      toast.error('No edited data to save');
      return;
    }

    this.savingAllRooms = true;
    const failedRooms: string[] = [];
    const successfulRooms: string[] = [];

    try {
      // Update shared filter first
      let sharedFilterSuccess = false;
      let cacheClearSuccess = false;
      if (this.shift && this.date) {
        // We got values for shared filter, so we clear the redis draft state and initiate the room schedule change process
        try {
          const response = await this.updateSharedFilter(this.shift, this.date);
          const responseCacheClear = await JobPasteServices.flushAllDrafts();
          cacheClearSuccess = true;
          sharedFilterSuccess = true;
        } catch (error) {
          toast.error('Failed to update shared filter');
          return;
        }
      } else {
        sharedFilterSuccess = true; // If no shift/date, consider it successful
      }
      // Process all rooms only if shared filter update was successful
      if (sharedFilterSuccess && cacheClearSuccess) {
        for (const room of this._cloned_jobs_for_edits) {
          try {
            const payload = this.prepareJobDataForSave(room.id);

            if (!payload) {
              failedRooms.push(`${room.name} (Failed to prepare data)`);
              continue;
            }

            // Save the room's jobs using CRDT endpoint
            const response = await JobPasteServices.saveEditedRoomJobs(payload as any);

            if (response && response.status === 'success') {
              successfulRooms.push(room.name);

              // Update the frontend state with the merged data from Redis
              if (response.merged_data && response.merged_data.jobs) {
                this.updateRoomWithCRDTData(room.id, response.merged_data);
              }
            } else {
              const errorMessage =
                response?.error || response?.message || 'No response from server';
              failedRooms.push(`${room.name} (${errorMessage})`);
            }
          } catch (error: any) {
            failedRooms.push(`${room.name} (${error.message || 'Unknown error'})`);
          }
        }

        // Show results
        if (successfulRooms.length > 0) {
          toast.success(
            `Successfully updated ${successfulRooms.length} room(s): ${successfulRooms.join(', ')}`
          );
        }

        if (failedRooms.length > 0) {
          toast.error(`Failed to update ${failedRooms.length} room(s): ${failedRooms.join(', ')}`);
        }

        // If at least some rooms were successful, update the main jobs
        if (successfulRooms.length > 0) {
          // Assign cloned jobs to main jobs
          this.jobs = toJS(this._cloned_jobs_for_edits);

          // Exit edit mode
          this.isEditMode = false;
          this.currentRoomInEditMode = null;
          // WebSocket will automatically update the jobs list
        }
      }
    } catch (error: any) {
      handleApiError(error, 'Error occurred while saving all rooms');
    } finally {
      this.savingAllRooms = false;
      // Only clear cloned jobs if all operations completed successfully
      if (failedRooms.length === 0 && successfulRooms.length > 0) {
        this._cloned_jobs_for_edits = null;
      }
    }
  };

  /**
   * Apply filter and save all edited rooms
   * This method combines updating the shared filter with saving all room edits
   */
  applyFilterAndSaveAllRooms = async () => {
    // If we have cloned jobs (in edit mode), save all rooms
    this._cloned_jobs_for_edits = toJS(this.jobs);

    if (this._cloned_jobs_for_edits && this._cloned_jobs_for_edits.length > 0) {
      await this.saveAllEditedRooms();
    }
  };

  // Helper method to get current user
  private getCurrentUser(): string {
    // Get user name from localStorage (stored by UserStore when user logs in)
    return localStorage.getItem('user_name') || 'Unknown User';
  }

  // WebSocket methods for real-time updates
  updateJobFromWebSocket = (data: {
    jobId: string;
    roomId: string;
    changes: Record<string, any>;
    version: number;
  }) => {
    if (!this.jobs && this.isEditMode) return;

    runInAction(() => {
      const room = this.jobs && this.jobs.find(r => r.id === data.roomId);
      if (room && room.jobs) {
        const job = room.jobs.find(j => j.jobId === data.jobId);
        if (job) {
          // Apply changes from WebSocket
          Object.assign(job, data.changes);

          // Update version for optimistic concurrency control
          if (data.version) {
            (job as any).version = data.version;
          }
        }
      }
    });
  };

  addJobFromWebSocket = (data: any) => {
    if (!this.jobs) return;

    runInAction(() => {
      const room = this.jobs && this.jobs.find(r => r.id === data.roomId);
      if (room && room.jobs && data.job) {
        // Add new job to the room
        room.jobs.push(data.job);

        // Sort jobs by serial number if available
        if (data.job.serial) {
          room.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
        }
      }
    });
  };

  /**
   * Handle changes reverted message from WebSocket
   * Applies reverted values to jobs when another user cancels their changes
   */
  handleChangesReverted = (revertData: {
    room: string;
    revertedBy: string;
    reverts: Record<string, any>;
  }) => {
    if (!this.jobs) return;

    runInAction(() => {
      // Find the room by name
      const room = this.jobs?.find(r => r.name === revertData.room);
      console.log({ roomFromRevert: room });
      if (!room) return;

      let revertedCount = 0;
      const revertedJobs: string[] = [];

      // Apply reverts for each job
      Object.entries(revertData.reverts).forEach(([jobId, revertedFields]) => {
        const job = room.jobs.find(j => j.jobId === jobId);
        if (job) {
          // Apply each reverted field
          Object.entries(revertedFields as Record<string, any>).forEach(
            ([field, originalValue]) => {
              (job as any)[field] = originalValue;
            }
          );
          revertedJobs.push(jobId);
          revertedCount++;
        }
      });

      // Sort jobs by serial number
      room.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));

      // Force reactivity for main jobs data
      if (this.jobs) {
        this.jobs = [...this.jobs];
      }

      // Also update cloned data if user is editing this room
      if (
        this.isEditMode &&
        this._cloned_jobs_for_edits &&
        this.currentRoomInEditMode === room.id
      ) {
        const clonedRoom = this._cloned_jobs_for_edits.find(r => r.id === room.id);
        if (clonedRoom) {
          Object.entries(revertData.reverts).forEach(([jobId, revertedFields]) => {
            const clonedJob = clonedRoom.jobs.find(j => j.jobId === jobId);
            if (clonedJob) {
              Object.entries(revertedFields as Record<string, any>).forEach(
                ([field, originalValue]) => {
                  (clonedJob as any)[field] = originalValue;
                }
              );
            }
          });

          // Sort cloned jobs by serial number
          clonedRoom.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));

          // Force MobX to detect changes by creating a new array reference
          this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
        }
      }

      // Show toast notification
      if (revertedCount > 0) {
        const jobText = revertedCount === 1 ? '1 job' : `${revertedCount} jobs`;
        toast.info(
          `${revertData.revertedBy} reverted changes in ${jobText} (${revertedJobs.join(', ')})`,
          {
            toastId: `revert-${revertData.revertedBy}-${Date.now()}`,
            autoClose: 5000
          }
        );
      }
    });
  };

  removeJobFromWebSocket = (data: { jobId: string; roomId: string }) => {
    if (!this.jobs) return;

    runInAction(() => {
      const room = this.jobs && this.jobs.find(r => r.id === data.roomId);
      if (room && room.jobs) {
        const jobIndex = room.jobs.findIndex(j => j.jobId === data.jobId);
        if (jobIndex !== -1) {
          room.jobs.splice(jobIndex, 1);
        }
      }
    });
  };

  updateRoomFromWebSocket = (data: any) => {
    if (!this.jobs) return;

    runInAction(() => {
      const roomIndex = this.jobs && this.jobs.findIndex(r => r.id === data.roomId);
      if (roomIndex !== -1 && this.jobs && typeof roomIndex === 'number' && this.jobs[roomIndex]) {
        // Update room properties
        Object.assign(this.jobs[roomIndex], data.changes);

        // Sort notes for each job in descending order (newest first)
        if (this.jobs[roomIndex].jobs && Array.isArray(this.jobs[roomIndex].jobs)) {
          this.jobs[roomIndex].jobs.forEach((job: any) => {
            if (Array.isArray(job.notes)) {
              job.notes.sort((a: any, b: any) => {
                // If notes have a timestamp or date property, sort by it
                // Try 'timestamp', then 'createdAt', then fallback to 0
                const aTime = a.timestamp ?? a.createdAt ?? 0;
                const bTime = b.timestamp ?? b.createdAt ?? 0;
                return bTime - aTime;
              });
            }
          });
        }
      }
    });
  };

  // Handle bulk jobs update from WebSocket
  handleBulkJobsUpdate = (jobs: any[]) => {
    if (!this.jobs) return;

    runInAction(() => {
      // Group jobs by room
      const jobsByRoom = jobs.reduce(
        (acc, job) => {
          if (!acc[job.room]) {
            acc[job.room] = [];
          }
          acc[job.room].push(job);
          return acc;
        },
        {} as Record<string, any[]>
      );

      // Update each room with new jobs
      Object.entries(jobsByRoom).forEach(([roomCode, roomJobs]) => {
        const typedRoomJobs = roomJobs as any[];
        let room = this.jobs && this.jobs.find(r => r.shortCode === roomCode);

        // If room doesn't exist, create it
        if (!room && this.jobs) {
          room = {
            id: roomCode,
            name: roomCode,
            shortCode: roomCode,
            color: '#666666', // Default color
            jobs: [],
            RoomTimeTotal: { days: 0, hours: 0, minutes: 0 },
            isExpanded: false
          };
          this.jobs.push(room);
        }

        if (room) {
          // Add new jobs to the room
          typedRoomJobs.forEach((newJob: any) => {
            const existingJobIndex = room!.jobs.findIndex(j => j.jobId === newJob.jobId);
            if (existingJobIndex !== -1) {
              // Update existing job
              room!.jobs[existingJobIndex] = newJob;
            } else {
              // Add new job
              room!.jobs.push(newJob);
            }
          });

          // Sort jobs by serial number
          room.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
        }
      });

      // If we're in edit mode, also update the cloned jobs data
      if (this.isEditMode && this._cloned_jobs_for_edits) {
        Object.entries(jobsByRoom).forEach(([roomCode, roomJobs]) => {
          const typedRoomJobs = roomJobs as any[];
          let clonedRoom =
            this._cloned_jobs_for_edits &&
            this._cloned_jobs_for_edits.find(r => r.shortCode === roomCode);

          // If room doesn't exist in cloned data, create it
          if (!clonedRoom) {
            clonedRoom = {
              id: roomCode,
              name: roomCode,
              shortCode: roomCode,
              color: '#666666', // Default color
              jobs: [],
              RoomTimeTotal: { days: 0, hours: 0, minutes: 0 },
              isExpanded: false
            };
            this._cloned_jobs_for_edits && this._cloned_jobs_for_edits.push(clonedRoom);
          }

          if (clonedRoom) {
            // Add new jobs to the cloned room
            typedRoomJobs.forEach((newJob: any) => {
              const existingJobIndex = clonedRoom!.jobs.findIndex(j => j.jobId === newJob.jobId);
              if (existingJobIndex !== -1) {
                // Update existing job in cloned data
                clonedRoom!.jobs[existingJobIndex] = newJob;
              } else {
                // Add new job to cloned data
                clonedRoom!.jobs.push(newJob);
              }
            });

            // Sort jobs by serial number
            clonedRoom.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
          }
        });
      }

      // No need to refresh jobs list - WebSocket will handle updates
    });
  };

  // Handle single job saved from WebSocket
  handleSingleJobSaved = (job: any, operation: 'create' | 'update') => {
    if (!this.jobs) return;

    runInAction(() => {
      let room = this.jobs && this.jobs.find(r => r.shortCode === job.room);

      // If room doesn't exist and this is a create operation, create it
      if (!room && operation === 'create' && this.jobs) {
        room = {
          id: job.room,
          name: job.room,
          shortCode: job.room,
          color: '#666666', // Default color
          jobs: [],
          RoomTimeTotal: { days: 0, hours: 0, minutes: 0 },
          isExpanded: false
        };
        this.jobs.push(room);
      }

      if (room) {
        const existingJobIndex = room.jobs.findIndex(j => j.jobId === job.jobId);

        if (operation === 'create' && existingJobIndex === -1) {
          // Add new job
          room.jobs.push(job);
          room.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
        } else if (operation === 'update' && existingJobIndex !== -1) {
          // Update existing job
          room.jobs[existingJobIndex] = job;
        }
      }

      // If we're in edit mode, also update the cloned jobs data
      if (this.isEditMode && this._cloned_jobs_for_edits) {
        let clonedRoom = this._cloned_jobs_for_edits.find(r => r.shortCode === job.room);

        // If room doesn't exist in cloned data and this is a create operation, create it
        if (!clonedRoom && operation === 'create') {
          clonedRoom = {
            id: job.room,
            name: job.room,
            shortCode: job.room,
            color: '#666666', // Default color
            jobs: [],
            RoomTimeTotal: { days: 0, hours: 0, minutes: 0 },
            isExpanded: false
          };
          this._cloned_jobs_for_edits.push(clonedRoom);
        }

        if (clonedRoom) {
          const existingJobIndex = clonedRoom.jobs.findIndex(j => j.jobId === job.jobId);

          if (operation === 'create' && existingJobIndex === -1) {
            // Add new job to cloned data
            clonedRoom.jobs.push(job);
            clonedRoom.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
          } else if (operation === 'update' && existingJobIndex !== -1) {
            // Update existing job in cloned data
            clonedRoom.jobs[existingJobIndex] = job;
          }
        }
      }

      // No need to refresh jobs list - WebSocket will handle updates
    });
  };

  /**
   * Handle jobs edited from WebSocket
   *
   * Updates the jobs store when jobs within a specific room are edited by other users.
   * This method handles both the main jobs data and the cloned jobs data used during edit mode.
   *
   * @param roomId - ID of the room that was edited
   * @param jobs - Array of updated job objects
   *
   * Process:
   * 1. Find the room in the main jobs data
   * 2. Update existing jobs or add new jobs to the room
   * 3. Sort jobs by serial number to maintain order
   * 4. If in edit mode, also update the cloned jobs data
   * 5. Maintain data consistency between main and cloned data
   */
  handleJobsEdited = (roomId: string, jobs: any[]) => {
    if (!this.jobs) return;

    runInAction(() => {
      const room = this.jobs && this.jobs.find(r => r.id === roomId);

      if (room) {
        // Update all jobs in the room
        jobs.forEach(updatedJob => {
          const existingJobIndex = room.jobs.findIndex(j => j.jobId === updatedJob.jobId);
          if (existingJobIndex !== -1) {
            // Update existing job
            room.jobs[existingJobIndex] = updatedJob;
          } else {
            // Add new job if it doesn't exist
            room.jobs.push(updatedJob);
          }
        });

        // Sort jobs by serial number
        room.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
      }

      // If we're in edit mode, also update the cloned jobs data
      if (this.isEditMode && this._cloned_jobs_for_edits) {
        const clonedRoom = this._cloned_jobs_for_edits.find(r => r.id === roomId);
        if (clonedRoom) {
          jobs.forEach(updatedJob => {
            const existingJobIndex = clonedRoom.jobs.findIndex(j => j.jobId === updatedJob.jobId);
            if (existingJobIndex !== -1) {
              // Update existing job in cloned data
              clonedRoom.jobs[existingJobIndex] = updatedJob;
            } else {
              // Add new job if it doesn't exist
              clonedRoom.jobs.push(updatedJob);
            }
          });
          // Sort jobs by serial number
          clonedRoom.jobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));
        }
      }
    });
  };

  /**
   * Helper method to preserve room colors from existing data
   *
   * This method ensures that room colors are maintained when updating from WebSocket data
   * or other sources that don't include color information.
   *
   * @param newRooms - Array of new room data without colors
   * @returns Array of rooms with preserved colors
   */
  private preserveRoomColors = (newRooms: any[]): any[] => {
    return newRooms.map(newRoom => {
      // Find existing room with the same ID to preserve its color
      const existingRoom = this.jobs?.find(existingRoom => existingRoom.id === newRoom.id);
      return {
        ...newRoom,
        color: existingRoom?.color || '#CCCCCC' // Use existing color or default gray
      };
    });
  };

  /**
   * Group a flat array of jobs by room
   * This is used when WebSocket data comes as a flat array instead of grouped by rooms
   */
  private groupJobsByRoom = (jobs: any[]): any[] => {
    const roomMap = new Map();

    jobs.forEach(job => {
      const roomId = job.room || job.roomId || 'unknown';

      if (!roomMap.has(roomId)) {
        roomMap.set(roomId, {
          id: roomId,
          name: roomId,
          shortCode: roomId,
          jobs: []
        });
      }

      roomMap.get(roomId).jobs.push(job);
    });

    return Array.from(roomMap.values());
  };

  /**
   * Update jobs from WebSocket
   *
   * This method updates the jobs data received from WebSocket broadcasts.
   * It provides the fastest way to update the UI with the latest data.
   *
   * @param completeJobsData - Complete jobs data from WebSocket
   *
   * Features:
   * - Handles various data structures (rooms array, flat jobs array, etc.)
   * - Validates data structure before processing
   * - Preserves room colors and edit mode state
   * - Automatically updates UI via MobX reactivity
   * - Includes comprehensive error handling and logging
   *
   * Usage:
   * Called by WebSocket message handlers when receiving job updates from server.
   * This method provides the fastest way to update the UI with the latest data.
   *
   * Note:
   * - This method completely replaces the jobs data, not merges it
   * - Edit mode is preserved by updating the cloned data structure
   * - All UI components will automatically update via MobX reactivity
   * - Rooms are sorted by ID to maintain consistent ordering
   * - Room colors are preserved from existing data to maintain visual consistency
   */
  updateJobsFromWebSocket = (completeJobsData: any[]) => {
    // Handle both string and object data from WebSocket
    let jobsData: any = completeJobsData;
    if (typeof completeJobsData === 'string') {
      try {
        jobsData = JSON.parse(completeJobsData);
      } catch {
        return;
      }
    } else if (completeJobsData && typeof completeJobsData === 'object') {
      // Data is already an object, just use it directly
      // formatJobStartTime is for individual job objects, not complete data arrays
      jobsData = completeJobsData;
    }

    if (!jobsData) {
      return;
    }

    // Determine the correct data structure
    let roomsArray: any[] = [];

    if (Array.isArray(jobsData)) {
      // If jobsData is already an array, check if it's an array of rooms
      if (jobsData.length > 0 && jobsData[0].jobs && Array.isArray(jobsData[0].jobs)) {
        // This looks like an array of rooms with jobs
        roomsArray = jobsData;
      } else {
        // This might be a flat array of jobs, we need to group them by room
        roomsArray = this.groupJobsByRoom(jobsData);
      }
    } else if (jobsData && typeof jobsData === 'object') {
      if (jobsData.rooms && Array.isArray(jobsData.rooms)) {
        // Data has a rooms property
        roomsArray = jobsData.rooms;
      } else if (jobsData.jobs && Array.isArray(jobsData.jobs)) {
        // Data has a jobs property, group them by room
        roomsArray = this.groupJobsByRoom(jobsData.jobs);
      } else {
        return;
      }
    } else {
      return;
    }

    // Validate that we have a proper rooms array
    if (!Array.isArray(roomsArray) || roomsArray.length === 0) {
      return;
    }

    // Validate each room has the expected structure
    const validRooms = roomsArray.filter(room => {
      if (!room || typeof room !== 'object') return false;
      if (!room.id || !room.jobs || !Array.isArray(room.jobs)) return false;
      return true;
    });

    if (validRooms.length === 0) {
      return;
    }

    runInAction(() => {
      try {
        // Preserve room colors from existing data
        const roomsWithPreservedColors = this.preserveRoomColors(validRooms);

        // Sort the rooms by ID to maintain consistent ordering
        const sortedJobsData = [...roomsWithPreservedColors].sort((a, b) => {
          // Convert room IDs to numbers for proper numeric sorting
          const roomIdA = parseInt(a.id, 10);
          const roomIdB = parseInt(b.id, 10);
          return roomIdA - roomIdB;
        });

        // Sort notes for each job in each room by updatedAt timestamp
        sortedJobsData.forEach((room: any) => {
          if (room.jobs && Array.isArray(room.jobs)) {
            // Sort jobs by serial number in ascending order
            room.jobs.sort((a: any, b: any) => (a.serial || 0) - (b.serial || 0));

            room.jobs.forEach((job: any) => {
              // Format jobStartTime for display
              if (job.jobStartTime && typeof job.jobStartTime === 'string') {
                // Keep the original ISO format for backend processing if needed
                job.jobStartTimeISO = job.jobStartTime;
                // Format for display
                job.jobStartTime = displayISODateWithoutTimezoneConversion(job.jobStartTime);
              }

              // Format confirmedDate for display
              if (job.confirmedDate && typeof job.confirmedDate === 'string') {
                job.confirmedDate = formatDateToMMDDYYYY(job.confirmedDate);
              }

              if (job.notes && Array.isArray(job.notes)) {
                job.notes.sort((noteA: any, noteB: any) => {
                  const updatedAtA = noteA.updatedAt || noteA.createdAt || '';
                  const updatedAtB = noteB.updatedAt || noteB.createdAt || '';

                  // Convert timestamp strings to Date objects for comparison
                  const dateA = new Date(updatedAtA);
                  const dateB = new Date(updatedAtB);

                  // Sort in descending order (newest first)
                  return dateB.getTime() - dateA.getTime();
                });
              }
            });
          }
        });

        // Handle WebSocket updates based on edit mode
        if (this.isEditMode) {
          this.handleWebSocketUpdateInEditMode(sortedJobsData);
        } else {
          // Normal update when not in edit mode
          this.jobs = null; // Clear first to trigger reactivity
          this.jobs = sortedJobsData.slice(); // Then set new data
        }
      } catch (error) {
        if (this.environment === 'development') {
          console.error('Error updating jobs from WebSocket:', error);
        }
        toast.error('Error updating jobs from WebSocket!');
      }
    });
  };

  /**
   * Handle WebSocket updates when user is in edit mode
   *
   * This method implements the specific logic for handling WebSocket updates during edit mode:
   * - For other rooms (not in edit mode): perform normal updates (jobs, cloned jobs)
   * - For the room which is in edit mode: check number of jobs and handle accordingly:
   *   - More jobs arrived: append new jobs to both data structures
   *   - Less jobs arrived: remove jobs not present in socket emission from both structures
   *   - Equal number of jobs: do not perform any updates
   *
   * @param sortedJobsData - The processed and sorted jobs data from WebSocket
   */
  private handleWebSocketUpdateInEditMode = (sortedJobsData: any[]) => {
    if (!this.isEditMode || !this._cloned_jobs_for_edits || !this.currentRoomInEditMode) {
      return;
    }

    // Update other rooms (not in edit mode) with normal updates
    const otherRooms = sortedJobsData.filter(room => room.id !== this.currentRoomInEditMode);
    const currentRoomInEdit = sortedJobsData.find(room => room.id === this.currentRoomInEditMode);

    // Update main jobs data for other rooms
    if (this.jobs) {
      otherRooms.forEach(wsRoom => {
        const existingRoomIndex = this.jobs!.findIndex(room => room.id === wsRoom.id);
        if (existingRoomIndex !== -1) {
          // Update existing room
          this.jobs![existingRoomIndex] = { ...wsRoom };
        } else {
          // Add new room if it doesn't exist
          this.jobs!.push({ ...wsRoom });
        }
      });
    }

    // Update cloned jobs data for other rooms
    otherRooms.forEach(wsRoom => {
      const existingClonedRoomIndex = this._cloned_jobs_for_edits!.findIndex(
        room => room.id === wsRoom.id
      );
      if (existingClonedRoomIndex !== -1) {
        // Update existing cloned room
        this._cloned_jobs_for_edits![existingClonedRoomIndex] = { ...wsRoom };
      } else {
        // Add new room to cloned data if it doesn't exist
        this._cloned_jobs_for_edits!.push({ ...wsRoom });
      }
    });

    // Handle the room that's currently in edit mode
    if (currentRoomInEdit) {
      const currentClonedRoom = this._cloned_jobs_for_edits.find(
        room => room.id === this.currentRoomInEditMode
      );

      if (currentClonedRoom) {
        const currentJobCount = currentClonedRoom.jobs.length;
        const incomingJobCount = currentRoomInEdit.jobs.length;

        if (incomingJobCount > currentJobCount) {
          // More jobs arrived - append new jobs to both data structures
          this.appendNewJobsToEditModeRoom(currentRoomInEdit, currentClonedRoom);
        } else if (incomingJobCount < currentJobCount) {
          // Less jobs arrived - remove jobs not present in socket emission
          this.removeMissingJobsFromEditModeRoom(currentRoomInEdit, currentClonedRoom);
        }
        // If equal number of jobs, do nothing (no updates needed)
      }
    }

    // Force MobX reactivity by creating new array references
    if (this.jobs) {
      this.jobs = [...this.jobs];
    }
    if (this._cloned_jobs_for_edits) {
      this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
    }
  };

  /**
   * Append new jobs to the room currently in edit mode
   * Updates both main jobs and cloned jobs data structures to maintain parity with socket updates
   *
   * @param wsRoom - Room data from WebSocket
   * @param clonedRoom - Current cloned room data
   */
  private appendNewJobsToEditModeRoom = (wsRoom: any, clonedRoom: any) => {
    const existingJobIds = new Set(clonedRoom.jobs.map((job: any) => job.jobId));
    const newJobs = wsRoom.jobs.filter((job: any) => !existingJobIds.has(job.jobId));

    if (newJobs.length > 0) {
      // Add new jobs to cloned room
      clonedRoom.jobs.push(...newJobs);

      // Also add new jobs to main jobs data to maintain parity
      if (this.jobs) {
        const mainRoom = this.jobs.find(room => room.id === this.currentRoomInEditMode);
        if (mainRoom) {
          mainRoom.jobs.push(...newJobs);
        }
      }

      // Sort jobs by serial number to maintain order in both data structures
      clonedRoom.jobs.sort((a: any, b: any) => (a.serial || 0) - (b.serial || 0));
      if (this.jobs) {
        const mainRoom = this.jobs.find(room => room.id === this.currentRoomInEditMode);
        if (mainRoom) {
          mainRoom.jobs.sort((a: any, b: any) => (a.serial || 0) - (b.serial || 0));
        }
      }
    }
  };

  /**
   * Remove jobs that are not present in the WebSocket emission
   * Updates both main jobs and cloned jobs data structures to maintain parity with socket updates
   *
   * @param wsRoom - Room data from WebSocket
   * @param clonedRoom - Current cloned room data
   */
  private removeMissingJobsFromEditModeRoom = (wsRoom: any, clonedRoom: any) => {
    const wsJobIds = new Set(wsRoom.jobs.map((job: any) => job.jobId));

    // Remove jobs from cloned room that are not in WebSocket data
    clonedRoom.jobs = clonedRoom.jobs.filter((job: any) => wsJobIds.has(job.jobId));

    // Also remove jobs from main jobs data to maintain parity
    if (this.jobs) {
      const mainRoom = this.jobs.find(room => room.id === this.currentRoomInEditMode);
      if (mainRoom) {
        mainRoom.jobs = mainRoom.jobs.filter((job: any) => wsJobIds.has(job.jobId));
      }
    }

    // Sort jobs by serial number to maintain order in both data structures
    clonedRoom.jobs.sort((a: any, b: any) => (a.serial || 0) - (b.serial || 0));
    if (this.jobs) {
      const mainRoom = this.jobs.find(room => room.id === this.currentRoomInEditMode);
      if (mainRoom) {
        mainRoom.jobs.sort((a: any, b: any) => (a.serial || 0) - (b.serial || 0));
      }
    }
  };

  /**
   * Update active users from WebSocket
   *
   * This method updates the active users data received from WebSocket broadcasts.
   * It maintains the current state of users editing each room for real-time display.
   *
   * @param activeUsersData - Active users data from WebSocket
   *
   * Features:
   * - Updates active users count for each room
   * - Maintains user information for display
   * - Enables real-time collaborative editing awareness
   * - Automatically updates UI via MobX reactivity
   */
  updateActiveUsersFromWebSocket = (activeUsersData: {
    [roomId: string]: {
      userCount: number;
      users: {
        [userId: string]: {
          username: string;
          joined_at: string;
        };
      };
    };
  }) => {
    runInAction(() => {
      this.activeUsersByRoom = activeUsersData;
    });
  };

  /**
   * Get active user count for a specific room
   *
   * @param roomId - The ID of the room
   * @returns The number of active users in the room, or 0 if no users
   */
  getActiveUserCount = (roomId: string): number => {
    const roomData = this.activeUsersByRoom[roomId];
    return roomData ? roomData.userCount : 0;
  };

  /**
   * Get active users for a specific room
   *
   * @param roomId - The ID of the room
   * @returns Array of active users in the room
   */
  getActiveUsers = (roomId: string): Array<{ username: string; joined_at: string }> => {
    const roomData = this.activeUsersByRoom[roomId];
    if (!roomData || !roomData.users) return [];
    return Object.values(roomData.users);
  };

  /**
   * Handle job reorder updates from WebSocket
   *
   * This method handles real-time job reordering updates from other users.
   * It updates the job serials and reorders jobs in both main and cloned data.
   *
   * @param roomName - Name of the room where reordering occurred
   * @param jobSerials - Object mapping jobId to new serial number
   * @param reorderedBy - Username of the user who initiated the reorder
   */
  handleJobReorderFromWebSocket = (
    roomName: string,
    jobSerials: Record<string, number>,
    reorderedBy: string
  ) => {
    if (!this.jobs) {
      return;
    }

    runInAction(() => {
      try {
        // Find the room by name in both main and cloned data
        const room = this.jobs?.find(r => r.name === roomName);
        const clonedRoom = this._cloned_jobs_for_edits?.find(r => r.name === roomName);

        // Update main jobs data
        if (room) {
          // Create a new jobs array to ensure MobX reactivity
          const updatedMainJobs = room.jobs.map(job => {
            if (job.jobId && jobSerials[job.jobId] !== undefined) {
              return { ...job, serial: jobSerials[job.jobId] };
            }
            return job;
          });

          // Sort by new serial numbers
          updatedMainJobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));

          // Replace the jobs array to trigger MobX reactivity
          room.jobs = updatedMainJobs;
        }

        // Update cloned data (this is what's used during edit mode)
        if (clonedRoom) {
          // Create a new jobs array to ensure MobX reactivity
          const updatedClonedJobs = clonedRoom.jobs.map(job => {
            if (job.jobId && jobSerials[job.jobId] !== undefined) {
              return { ...job, serial: jobSerials[job.jobId] };
            }
            return job;
          });

          // Sort by new serial numbers
          updatedClonedJobs.sort((a, b) => (a.serial || 0) - (b.serial || 0));

          // Replace the jobs array to trigger MobX reactivity
          clonedRoom.jobs = updatedClonedJobs;
        }

        // Force MobX to recognize changes by creating new array references
        if (this.jobs) {
          this.jobs = [...this.jobs];
        }
        if (this._cloned_jobs_for_edits) {
          this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
        }

        // Log successful reorder for debugging
      } catch (error) {
        console.error('🔄 Error handling job reorder from WebSocket:', error);
      }
    });
  };

  /**
   * Handle field value changes from YJS collaboration WebSocket
   * Updates the respective fields in both main jobs and cloned jobs data
   *
   * @param roomName - Name of the room containing the job
   * @param jobId - ID of the job being updated
   * @param fieldName - Name of the field being updated
   * @param fieldValue - New value for the field
   * @param updatedBy - Username of the user who made the change
   * @param timestamp - Timestamp of the change
   */
  handleFieldValueChangeFromYJS = (
    roomName: string,
    jobId: string,
    fieldName: string,
    fieldValue: any,
    updatedBy: string,
    timestamp: string
  ) => {
    // console.log('🔔 JobPasteStore: handleFieldValueChangeFromYJS called with:', {
    //   roomName,
    //   jobId,
    //   fieldName,
    //   fieldValue,
    //   updatedBy,
    //   timestamp,
    //   hasMainJobs: !!this.jobs,
    //   hasClonedJobs: !!this._cloned_jobs_for_edits,
    //   isEditMode: this.isEditMode
    // });

    // Debug: Log available room names and job IDs
    if (this.jobs) {
      // console.log(
      //   '🔔 JobPasteStore: Available room names:',
      //   this.jobs.map(r => r.name)
      // );
      // console.log(
      //   '🔔 JobPasteStore: Available job IDs in all rooms:',
      //   this.jobs.flatMap(r => r.jobs.map(j => j.id))
      // );
    }

    if (!this.jobs) {
      // console.warn('🔔 JobPasteStore: No main jobs data available for field update');
      return;
    }

    runInAction(() => {
      try {
        // Find the room by name in main jobs data
        const room = this.jobs?.find(r => r.name === roomName);

        if (room) {
          const job = room.jobs.find(j => j.jobId === jobId);

          if (job) {
            // Update the field value in main jobs data
            (job as any)[fieldName] = fieldValue;
          } else {
            console.warn('❌ STORE WARNING: Job not found in main jobs data', {
              roomName,
              jobId,
              availableJobIds: room.jobs.map(j => j.jobId)
            });
          }
        } else {
          console.warn('❌ STORE WARNING: Room not found in main jobs data', {
            roomName,
            availableRooms: this.jobs?.map(r => r.name) || []
          });
        }

        // Also update cloned jobs data if it exists (during edit mode)
        if (this._cloned_jobs_for_edits) {
          const clonedRoom = this._cloned_jobs_for_edits.find(r => r.name === roomName);

          if (clonedRoom) {
            const clonedJob = clonedRoom.jobs.find(j => j.jobId === jobId);

            if (clonedJob) {
              // Update the field value in cloned jobs data
              (clonedJob as any)[fieldName] = fieldValue;
            } else {
              console.warn('❌ STORE WARNING: Job not found in cloned jobs data', {
                roomName,
                jobId,
                availableJobIds: clonedRoom.jobs.map(j => j.jobId)
              });
            }
          } else {
            console.warn('❌ STORE WARNING: Room not found in cloned jobs data', {
              roomName,
              availableRooms: this._cloned_jobs_for_edits?.map(r => r.name) || []
            });
          }
        }

        // Force MobX reactivity by creating new array references
        if (this.jobs) {
          this.jobs = [...this.jobs];
        }
        if (this._cloned_jobs_for_edits) {
          this._cloned_jobs_for_edits = [...this._cloned_jobs_for_edits];
        }
      } catch (error) {
        console.error('❌ Error handling field value change from YJS:', error);
      }
    });
  };

  /**
   * Check if a specific room is currently loading for Redis check
   *
   * @param roomId - The ID of the room
   * @returns Boolean indicating if the room is loading
   */
  isRoomLoading = (roomId: string): boolean => {
    if (!roomId) return false;
    return this.redisCheckLoadingByRoom.get(roomId) || false;
  };

  /**
   * Fetch all rooms data from Redis and update the jobs
   * This method is called when user clicks cancel to refresh data from Redis
   * Only updates specific fields for existing jobs to preserve other data
   */
  fetchAllRoomsFromRedis = async (): Promise<void> => {
    try {
      this.setJobsLoading(true);

      const response = await JobPasteServices.fetchAllRoomsFromRedis();

      if (response.success && response.rooms) {
        const sortedJobsData = response.rooms.sort((a: any, b: any) => a.id - b.id);
        runInAction(() => {
          // Process the Redis data and merge with existing jobs
          const mergedRooms = this.mergeRedisDataWithExistingJobs(sortedJobsData);

          // Update the jobs with merged data from Redis
          this.jobs = mergedRooms.slice();

          // Clear any cloned jobs since we're refreshing from Redis
          this._cloned_jobs_for_edits = null;

          // Exit edit mode
          this.isEditMode = false;
          this.currentRoomInEditMode = null;

          // Clear field change tracker
          this.fieldChangeTracker.clear();
        });
      } else {
        toast.error('Failed to fetch data from Redis');
      }
    } catch (error: any) {
      handleApiError(error, 'Error fetching rooms from Redis');
    } finally {
      this.setJobsLoading(false);
    }
  };

  /**
   * Merge Redis data with existing jobs, updating only specific fields
   * This method preserves existing job data and only updates the specified fields from Redis
   */
  private mergeRedisDataWithExistingJobs = (redisRooms: any[]): any[] => {
    // Preserve room colors first
    const roomsWithPreservedColors = this.preserveRoomColors(redisRooms);

    // Define the fields that should be updated from Redis
    const updatableFields = [
      'priority',
      'room',
      'status',
      'bpmOverride',
      'cleanOverride',
      'numberOfBottlesInAtShiftStart',
      'jobStartTime'
    ];

    return roomsWithPreservedColors.map((redisRoom: any) => {
      // Find the existing room in current jobs
      const existingRoom = this.jobs?.find(existingRoom => existingRoom.id === redisRoom.id);

      if (!existingRoom || !redisRoom.jobs || !Array.isArray(redisRoom.jobs)) {
        // If no existing room or no jobs in Redis data, use Redis data as-is
        return this.processRedisDataForFrontend([redisRoom])[0];
      }

      // Process each job in the Redis room
      const mergedJobs = redisRoom.jobs.map((redisJob: any) => {
        // Find the existing job by jobId
        const existingJob = existingRoom.jobs.find(
          (existingJob: any) => existingJob.jobId === redisJob.jobId
        );

        if (!existingJob) {
          // If job doesn't exist in current data, use Redis job data
          return this.processJobData(redisJob);
        }

        // Merge existing job with Redis data, updating only specified fields
        const mergedJob = { ...existingJob };

        // Update only the specified fields from Redis data
        updatableFields.forEach(field => {
          if (redisJob.hasOwnProperty(field) && redisJob[field] !== undefined) {
            (mergedJob as any)[field] = redisJob[field];
          }
        });

        // Process the merged job data (format dates, etc.)
        return this.processJobData(mergedJob);
      });

      // Also include any existing jobs that are not in Redis data (preserve them)
      const existingJobIds = mergedJobs.map((job: any) => job.jobId);
      const preservedJobs = existingRoom.jobs.filter(
        (existingJob: any) => !existingJobIds.includes(existingJob.jobId)
      );

      // Combine merged jobs with preserved jobs
      const allJobs = [...mergedJobs, ...preservedJobs];

      // Return the room with merged jobs
      return {
        ...existingRoom,
        jobs: allJobs
      };
    });
  };

  /**
   * Process individual job data (format dates, etc.)
   */
  private processJobData = (job: any): any => {
    const processedJob = { ...job };

    // Format jobStartTime for display
    if (processedJob.jobStartTime && typeof processedJob.jobStartTime === 'string') {
      // Keep the original ISO format for backend processing if needed
      processedJob.jobStartTimeISO = processedJob.jobStartTime;
      // Format for display
      processedJob.jobStartTime = displayISODateWithoutTimezoneConversion(
        processedJob.jobStartTime
      );
    }

    // Format confirmedDate for display
    if (processedJob.confirmedDate && typeof processedJob.confirmedDate === 'string') {
      processedJob.confirmedDate = formatDateToMMDDYYYY(processedJob.confirmedDate);
    }

    // Process notes - ensure they are properly formatted
    if (processedJob.notes && Array.isArray(processedJob.notes)) {
      // Sort notes by timestamp (newest first)
      processedJob.notes.sort((noteA: any, noteB: any) => {
        const updatedAtA =
          noteA.updatedAt || noteA.createdAt || noteA.updated_at || noteA.created_at || '';
        const updatedAtB =
          noteB.updatedAt || noteB.createdAt || noteB.updated_at || noteB.created_at || '';

        // Convert timestamp strings to Date objects for comparison
        const dateA = new Date(updatedAtA);
        const dateB = new Date(updatedAtB);

        // Sort in descending order (newest first)
        return dateB.getTime() - dateA.getTime();
      });
    }

    return processedJob;
  };

  /**
   * Process Redis data for frontend display
   * This method processes the data similar to updateJobsFromWebSocket
   */
  private processRedisDataForFrontend = (rooms: any[]): any[] => {
    // Preserve room colors first
    const roomsWithPreservedColors = this.preserveRoomColors(rooms);

    // Process each room and job
    return roomsWithPreservedColors.map((room: any) => {
      if (room.jobs && Array.isArray(room.jobs)) {
        room.jobs.forEach((job: any) => {
          // Format jobStartTime for display
          if (job.jobStartTime && typeof job.jobStartTime === 'string') {
            // Keep the original ISO format for backend processing if needed
            job.jobStartTimeISO = job.jobStartTime;
            // Format for display
            job.jobStartTime = displayISODateWithoutTimezoneConversion(job.jobStartTime);
            job.confirmedDate = formatDateToMMDDYYYY(job.confirmedDate);
          }

          // Process notes - ensure they are properly formatted
          if (job.notes && Array.isArray(job.notes)) {
            // Sort notes by timestamp (newest first)
            job.notes.sort((noteA: any, noteB: any) => {
              const updatedAtA =
                noteA.updatedAt || noteA.createdAt || noteA.updated_at || noteA.created_at || '';
              const updatedAtB =
                noteB.updatedAt || noteB.createdAt || noteB.updated_at || noteB.created_at || '';

              // Convert timestamp strings to Date objects for comparison
              const dateA = new Date(updatedAtA);
              const dateB = new Date(updatedAtB);

              // Sort in descending order (newest first)
              return dateB.getTime() - dateA.getTime();
            });
          }
        });
      }
      return room;
    });
  };
}
